import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { engagement, businesses as bizApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { MapPin, Loader2, CheckCircle2, XCircle, ShieldCheck, ArrowRight } from 'lucide-react';

/** Route: /reviews/checkin/:bid — user opens after scanning the business's
 *  on-site QR code. We request their device location and verify the backend
 *  says they're within 500m of the business. On success we issue a review
 *  permission and route them to the review form. */
export default function ReviewCheckin() {
  const { bid } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState('idle'); // idle | locating | verifying | ok | err
  const [err, setErr] = useState('');
  const [biz, setBiz] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    bizApi.get(bid).then(setBiz).catch(() => {});
  }, [bid]);

  useEffect(() => {
    if (!user) navigate(`/login?redirect=/reviews/checkin/${bid}`);
  }, [user, bid, navigate]);

  const startCheckin = () => {
    setErr(''); setState('locating');
    if (!('geolocation' in navigator)) {
      setErr('Location services aren\'t available in this browser.'); setState('err'); return;
    }
    navigator.geolocation.getCurrentPosition(async (pos) => {
      setState('verifying');
      try {
        const res = await engagement.reviewCheckin(bid, pos.coords.latitude, pos.coords.longitude);
        setResult(res); setState('ok');
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Check-in failed. Move closer to the business and try again.');
        setState('err');
      }
    }, (geoErr) => {
      setErr(geoErr.code === 1 ? 'You blocked location access. Enable it in your browser settings and try again.' : 'Couldn\'t read your location. Try again.');
      setState('err');
    }, { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center py-8">
      <div className="pbb-card p-6 w-full max-w-md space-y-4" data-testid="review-checkin">
        <div className="flex items-center gap-2">
          <ShieldCheck className="text-green-400" size={22}/>
          <div>
            <div className="font-semibold">Verify your visit</div>
            <div className="text-xs text-gray-400">Scan complete. We'll confirm you're at {biz?.name || 'the business'} using your device location.</div>
          </div>
        </div>

        {state === 'idle' && (
          <button onClick={startCheckin} data-testid="checkin-start" className="pbb-btn-green w-full inline-flex items-center justify-center gap-2">
            <MapPin size={16}/> Share my location & verify
          </button>
        )}
        {(state === 'locating' || state === 'verifying') && (
          <div className="rounded-lg bg-[#0d1f14] border border-[#153b23] p-4 text-sm text-gray-300 inline-flex items-center gap-2">
            <Loader2 className="animate-spin text-green-400" size={16}/>
            {state === 'locating' ? 'Reading your location…' : 'Verifying with the business…'}
          </div>
        )}
        {state === 'err' && (
          <div className="rounded-lg bg-red-600/10 border border-red-600/40 p-3 text-sm text-red-300 space-y-3" data-testid="checkin-err">
            <div className="inline-flex items-center gap-2"><XCircle size={16}/> {err}</div>
            <button onClick={startCheckin} className="px-3 py-2 rounded bg-red-500/20 border border-red-400/40 text-white text-sm">Try again</button>
          </div>
        )}
        {state === 'ok' && result && (
          <div className="space-y-3" data-testid="checkin-ok">
            <div className="rounded-lg bg-green-600/10 border border-green-600/40 p-3 text-sm text-green-300 inline-flex items-center gap-2">
              <CheckCircle2 size={16}/> Verified at {result.businessName} ({result.distance_m ?? 0} m). You can leave one review.
            </div>
            <Link to={`/business/${bid}`} data-testid="checkin-review-btn" className="pbb-btn-green w-full inline-flex items-center justify-center gap-2">
              Leave your review <ArrowRight size={14}/>
            </Link>
          </div>
        )}

        <div className="text-[11px] text-gray-500 pt-3 border-t border-[#1f1f1f]">
          Reviews are verified to keep listings honest. We only use your location once and never share it.
        </div>
      </div>
    </div>
  );
}
